// The TrailSafe design system's Day theme for MUI. The same values live as CSS
// custom properties in styles/variables.scss; MUI needs real colours (it
// derives hover and alpha shades from them), so they are repeated here.
export const colors = {
  surface: '#f5efe3',
  surfaceRaised: '#fffbf3',
  surfaceSunken: '#ebe3d3',
  line: '#ddd3c0',
  lineStrong: '#857a69',
  ink: '#29251f',
  inkMuted: '#5c5448',
  forest: '#11524e',
  forestStrong: '#0a3b38',
  forestSoft: '#dbe9e3',
  onForest: '#fffbf3',
  ochre: '#c98a2b',
  bark: '#3d3227',
  success: '#1f6b62',
  successInk: '#174f49',
  successSoft: '#d6ebe5',
  warning: '#c7861c',
  warningInk: '#6b4400',
  warningSoft: '#f7e3b6',
  danger: '#a33a1f',
  dangerInk: '#842c15',
  dangerSoft: '#f6dcd2',
  info: '#34647f',
  infoInk: '#244a60',
  infoSoft: '#dce8ee',
  focus: '#b0500a',
};

const display = ['Bitter', 'Rockwell', 'Georgia', 'serif'].join(',');
const sans = [
  '"Atkinson Hyperlegible Next"',
  '"Atkinson Hyperlegible"',
  '"Segoe UI"',
  'system-ui',
  'sans-serif',
].join(',');

const focusRing = {
  outline: `3px solid ${colors.focus}`,
  outlineOffset: 2,
};

export const themeSettings = () => {
  return {
    palette: {
      primary: {
        main: colors.forest,
        dark: colors.forestStrong,
        light: colors.forestSoft,
        contrastText: colors.onForest,
      },
      secondary: {
        main: colors.bark,
        contrastText: colors.surface,
      },
      error: {
        main: colors.danger,
        dark: colors.dangerInk,
        light: colors.dangerSoft,
        contrastText: colors.onForest,
      },
      warning: {
        main: colors.warning,
        dark: colors.warningInk,
        light: colors.warningSoft,
      },
      success: {
        main: colors.success,
        dark: colors.successInk,
        light: colors.successSoft,
      },
      info: { main: colors.info, dark: colors.infoInk, light: colors.infoSoft },
      text: { primary: colors.ink, secondary: colors.inkMuted },
      background: { default: colors.surface, paper: colors.surfaceRaised },
      divider: colors.line,
    },

    shape: { borderRadius: 10 },

    // Headings step down one size from MUI's defaults, so the existing
    // pages' h2 page titles land on the design system's h1 (30px).
    typography: {
      fontFamily: sans,
      fontSize: 16,
      h1: {
        fontFamily: display,
        fontSize: 40,
        lineHeight: '46px',
        fontWeight: 700,
      },
      h2: {
        fontFamily: display,
        fontSize: 30,
        lineHeight: '36px',
        fontWeight: 700,
      },
      h3: {
        fontFamily: display,
        fontSize: 24,
        lineHeight: '30px',
        fontWeight: 600,
      },
      h4: {
        fontFamily: display,
        fontSize: 20,
        lineHeight: '26px',
        fontWeight: 600,
      },
      h5: {
        fontFamily: sans,
        fontSize: 17,
        lineHeight: '26px',
        fontWeight: 700,
      },
      h6: {
        fontFamily: sans,
        fontSize: 16,
        lineHeight: '22px',
        fontWeight: 700,
      },
      body1: { fontSize: 17, lineHeight: '26px' },
      body2: { fontSize: 15, lineHeight: '22px' },
      caption: { fontSize: 15, lineHeight: '22px' },
      button: {
        fontSize: 17,
        lineHeight: '24px',
        fontWeight: 700,
        textTransform: 'none',
      },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: sans,
            color: colors.ink,
            backgroundColor: colors.surface,
          },
        },
      },
      MuiButtonBase: {
        defaultProps: { disableRipple: true },
        styleOverrides: { root: { '&.Mui-focusVisible': focusRing } },
      },
      MuiButton: {
        styleOverrides: {
          root: { minHeight: 48, borderRadius: 10, boxShadow: 'none' },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: colors.surfaceRaised,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.lineStrong,
              borderWidth: 2,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.inkMuted,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.forest,
            },
          },
          input: { minHeight: 24 },
        },
      },
      MuiInputLabel: {
        styleOverrides: { root: { color: colors.inkMuted } },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            minHeight: 44,
            padding: '0 12px',
            fontSize: 16,
            fontWeight: 700,
            textTransform: 'none',
            color: colors.ink,
            backgroundColor: colors.surfaceRaised,
            borderColor: colors.lineStrong,
            '&.Mui-selected, &.Mui-selected:hover': {
              color: colors.onForest,
              backgroundColor: colors.forest,
            },
            '&.Mui-disabled.Mui-selected': {
              color: colors.forest,
              backgroundColor: colors.forestSoft,
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: { root: { color: colors.lineStrong } },
      },
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: 'none' } },
      },
      MuiDataGrid: {
        styleOverrides: {
          row: {
            '&.Mui-selected': {
              backgroundColor: colors.forestSoft,
              color: colors.ink,
              '&:hover': { backgroundColor: colors.forestSoft },
            },
          },
        },
      },
    },
  };
};
